import { useState } from 'react';
import { TASK_SOURCE_LABELS } from '@shared/types/task-sources';
import type { TaskPushSource } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChoiceCardGroup } from '@/components/ChoiceCard';
import { TrackerIcon } from '@/components/TrackerIcon';

/**
 * SF-T-207: "Push to source…" from a task's ⋯ menu, for a top-level task made here. Pick one of the
 * project's enabled sources (the Board's meta, `push_sources`; picked already when there's one): Push
 * makes it there, with its sub-tasks under it, and mirrors them from there. The menu offers it only when
 * the project has one. `onPush` sends it (the caller's toast and reload); the dialog closes once it
 * resolves true.
 */
export function PushUpstreamDialog({
  open,
  onOpenChange,
  sources,
  subtasks = 0,
  onPush,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: TaskPushSource[];
  /** How many sub-tasks go with it, for the dialog's words. */
  subtasks?: number;
  onPush: (source: TaskPushSource) => Promise<boolean>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" description="Make this task on one of the project's sources, and mirror it from there.">
        <DialogHeader>
          <DialogTitle>Push to source</DialogTitle>
        </DialogHeader>
        {open ? <PickSource sources={sources} subtasks={subtasks} onPush={onPush} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function PickSource({
  sources,
  subtasks,
  onPush,
  onClose,
}: {
  sources: TaskPushSource[];
  subtasks: number;
  onPush: (source: TaskPushSource) => Promise<boolean>;
  onClose: () => void;
}) {
  const [pickedId, setPickedId] = useState('');
  const [pushing, setPushing] = useState(false);
  // One source: it's the one.
  const picked = sources.find((s) => s.id === pickedId) ?? (sources.length === 1 ? sources[0] : undefined);

  const push = async () => {
    if (!picked) return;
    setPushing(true);
    const ok = await onPush(picked);
    setPushing(false);
    if (ok) onClose();
  };

  return (
    <>
      <ChoiceCardGroup
        label="Which source?"
        size="sm"
        value={picked?.id ?? ''}
        onValueChange={setPickedId}
        options={sources.map((s) => ({
          value: s.id,
          title: s.name,
          description: TASK_SOURCE_LABELS[s.kind],
          media: <TrackerIcon kind={s.kind} className="mt-0.5 text-muted-foreground" />,
        }))}
      />
      <p className="text-xs text-muted-foreground">
        {subtasks ? `Its title and description go there, and its ${subtasks === 1 ? 'sub-task goes' : `${subtasks} sub-tasks go`} under it. ` : 'Its title and description go there. '}
        It’s mirrored from there after: the text is the source’s from then on, and moves and comments you choose to post go up. Comments and files stay here.
      </p>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!picked} disabledReason="Pick the source" loading={pushing} loadingText="Pushing…" onClick={() => void push()}>
          {picked ? `Push to ${TASK_SOURCE_LABELS[picked.kind]}` : 'Push'}
        </Button>
      </DialogFooter>
    </>
  );
}
